import { useEffect, useRef } from 'react';
import { jmrSquaredAttribution } from '../config/attribution';
import { demoPreview } from '../config/demo-preview';

// The hit area is a 44px pseudo-element: padding the link itself would make
// the sticky banner 24px taller on every screen.
const LINK_CLASSES =
  "relative inline-block font-medium underline decoration-amber-400 underline-offset-2 after:absolute after:inset-x-0 after:top-1/2 after:h-11 after:-translate-y-1/2 after:content-[''] hover:decoration-amber-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-800";

export function DemoPreviewBanner() {
  const bannerRef = useRef<HTMLDivElement>(null);

  /**
   * The header offsets itself by --demo-banner-height. A hardcoded guess goes
   * stale as soon as the banner copy wraps, and the banner then covers the
   * header and swallows taps on the menu button, so publish the real height.
   */
  useEffect(() => {
    const banner = bannerRef.current;

    if (!banner) {
      return;
    }

    const publishHeight = () => {
      document.documentElement.style.setProperty(
        '--demo-banner-height',
        `${Math.ceil(banner.getBoundingClientRect().height)}px`,
      );
    };

    publishHeight();

    const observer = new ResizeObserver(publishHeight);
    observer.observe(banner);

    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty('--demo-banner-height');
    };
  }, []);

  return (
    <div
      ref={bannerRef}
      className="sticky top-0 z-[60] border-b border-amber-300/80 bg-amber-50 px-4 py-2 text-amber-950 sm:px-6"
      role="status"
    >
      {/* Sticky chrome above the hero, so it stays one line at every width. The
          full disclosure and both studio links live in JmrSquaredAttribution;
          here the longer wording and the links wait for a width that holds them
          on the same line. */}
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-6 font-body text-sm leading-5">
        <p className="min-w-0">
          <span className="lg:hidden">
            <span className="font-semibold">{demoPreview.shortBadgeLabel}</span>
            {' · '}
            {demoPreview.shortMessage}
          </span>
          <span className="hidden lg:inline">
            <span className="font-semibold">{demoPreview.badgeLabel}</span>
            {' · '}
            {demoPreview.bannerMessage}
          </span>
        </p>
        <p className="hidden shrink-0 text-right xl:block">
          Questions?{' '}
          <a href={jmrSquaredAttribution.contactMailto} className={LINK_CLASSES}>
            {jmrSquaredAttribution.contactEmail}
          </a>
          {' · '}
          <a
            href={jmrSquaredAttribution.studioUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={LINK_CLASSES}
          >
            tech.jmrsquared.com
          </a>
        </p>
      </div>
    </div>
  );
}
